import {useMemo} from 'react'
import {useQuery} from '@github-ui/react-query'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {createPersister} from '@github-ui/react-query-persister'
import {Compile} from '@github-ui/react-query-persister/typebox/compile'
import {useAnalytics} from '@github-ui/use-analytics'
import {fetchCAPI} from '@github-ui/copilot-shared-ui/capi/fetch-capi'
import {environmentApiPath} from '../utils/api-url-helpers'
import {EnvironmentSchema, ENVIRONMENTS_MAX_AGE_MS} from '@github-ui/copilot-shared-ui/environments/use-environments'
import type {Environment} from '@github-ui/agent-sessions-types/environment'
import type {EstablishedAhpResumptionAttempt} from '@github-ui/ahp-session-client/contracts'
import {useEstablishedAhpResumptionAttemptError} from './use-established-ahp-resumption-attempt-error'

export const environmentQueryKey = ['agent-environment'] as const

const EnvironmentValidator = Compile(EnvironmentSchema)

interface UseEnvironmentOptions {
  enabled?: boolean
  resumptionAttempt?: EstablishedAhpResumptionAttempt
}

/**
 * Fetches a single AHP environment by id, resolving any kind — including the
 * per-session `managed-sandbox` allocations that {@link useEnvironments} omits.
 */
export function useEnvironment(
  environmentId: string | undefined,
  {enabled = true, resumptionAttempt}: UseEnvironmentOptions = {},
) {
  const flagEnabled = isFeatureEnabled('copilot_mission_control_environments_web_kickoff')
  const queryEnabled = flagEnabled && enabled && !!environmentId
  const {sendAnalyticsEvent} = useAnalytics()

  const persister = useMemo(
    () =>
      createPersister({
        validator: EnvironmentValidator,
        sendAnalyticsEvent,
        prefix: 'copilot-agent-environment',
        maxAgeMs: ENVIRONMENTS_MAX_AGE_MS,
        enabled: queryEnabled,
      }),
    [sendAnalyticsEvent, queryEnabled],
  )

  const query = useQuery<Environment | null>({
    queryKey: [...environmentQueryKey, environmentId ?? null],
    enabled: queryEnabled,
    queryFn: async ({signal}) => {
      if (!environmentId) return null
      const response = await fetchCAPI({path: environmentApiPath(environmentId), signal})
      const data = await response.json()
      if (!data || typeof data !== 'object' || Array.isArray(data)) return null
      // Strip `webpubsub` (carries an `access_token`) so it never hits the cache.
      const {webpubsub: _webpubsub, ...environment} = data as Environment
      return environment
    },
    persister,
  })
  const resumptionError = useEstablishedAhpResumptionAttemptError(queryEnabled ? resumptionAttempt : undefined)
  const hasTimelyEnvironment = !!query.data && (!resumptionAttempt || query.dataUpdatedAt < resumptionAttempt.expiresAt)

  return resumptionError && !hasTimelyEnvironment
    ? {
        ...query,
        data: undefined,
        error: resumptionError,
        isError: true,
        isLoading: false,
        isPending: false,
        status: 'error' as const,
      }
    : query
}
